import { parse } from 'yaml';

import type {
  EvictorAdvancedEvictionConfigEntry,
  EvictorLabelSelector,
  EvictorLabelSelectorExpression,
  EvictorLabelSelectorOperator,
  EvictorNodeSelector,
  EvictorPodSelector,
} from '@cast/types-workload-eviction';

import type { Result } from '../_types';

export const parseYaml = (yaml: string): Result<unknown, unknown> => {
  try {
    return { success: true, value: parse(yaml) };
  } catch (error) {
    return { success: false, error };
  }
};

export const operatorRequiresValues = (
  operator: EvictorLabelSelectorOperator
): boolean => operator === 'IN' || operator === 'NOT_IN';

export const isLabelKeyPresent = (
  label: EvictorLabelSelectorExpression
): boolean => !!label.key;

export const isLabelValuesComplete = (
  label: EvictorLabelSelectorExpression
): boolean => !operatorRequiresValues(label.operator) || !!label.values?.length;

export const isLabelValid = (label: EvictorLabelSelectorExpression): boolean =>
  isLabelKeyPresent(label) && isLabelValuesComplete(label);

const labelSelectorHasCriteria = (
  labelSelector: EvictorLabelSelector
): boolean => {
  const { matchLabels, matchExpressions } = labelSelector;

  if (matchLabels && Object.keys(matchLabels).length > 0) {
    return true;
  }

  return !!matchExpressions?.some(isLabelValid);
};

const podSelectorHasCriteria = (podSelector: EvictorPodSelector): boolean => {
  return (
    !!podSelector.namespace ||
    !!podSelector.kind ||
    podSelector.replicasMin !== undefined ||
    (!!podSelector.labelSelector &&
      labelSelectorHasCriteria(podSelector.labelSelector))
  );
};

const nodeSelectorHasCriteria = (nodeSelector: EvictorNodeSelector): boolean =>
  labelSelectorHasCriteria(nodeSelector.labelSelector);

export const entryHasSelectorCriteria = (
  entry: EvictorAdvancedEvictionConfigEntry
): boolean => {
  const { podSelector, nodeSelector } = entry;

  if (!podSelector && !nodeSelector) {
    return false;
  }

  if (podSelector && !podSelectorHasCriteria(podSelector)) {
    return false;
  }

  if (nodeSelector && !nodeSelectorHasCriteria(nodeSelector)) {
    return false;
  }

  return true;
};
